import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { DicomUtilitiesService } from '../services/dicom-utilities.service';
import { StudyRecord, FileRecord } from '../services/dicomdir-parser';
import { UploadLinkInfo, UploadLinksService } from '../services/upload-links.service';

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 3;

interface Item {
  file: File;
  relativePath: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'failed';
}

type State = 'loading' | 'invalid' | 'ready' | 'reading' | 'selected' | 'uploading' | 'uploaded' | 'finishing' | 'done' | 'error';

/**
 * Public page opened by the patient from a one-time upload link (route /upload/:token). There is no account and no session:
 * the token in the address is the credential. The page shows nothing about the firm, the case or earlier uploads.
 */
@Component({
  selector: 'app-upload-link-page',
  templateUrl: './upload-link-page.component.html',
  styleUrls: ['./upload-link-page.component.scss'],
})
export class UploadLinkPageComponent implements OnInit {
  @ViewChild('folderInput') folderInput: ElementRef<HTMLInputElement>;
  @ViewChild('filesInput') filesInput: ElementRef<HTMLInputElement>;

  state: State = 'loading';
  info: UploadLinkInfo | null = null;
  items: Item[] = [];
  message = '';
  /** Shown once the upload is closed: the only status the uploader ever sees is "received". */
  receivedCount = 0;
  receivedAt: Date | null = null;

  private token = '';
  private studies: StudyRecord[] = [];
  private dicomdirRelativePath: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private links: UploadLinksService,
    private dicom_$: DicomUtilitiesService,
    private cd: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    try {
      this.info = await this.links.info(this.token);
      this.state = 'ready';
    } catch (e) {
      // Unknown, expired, used and revoked links all look the same on purpose.
      this.state = 'invalid';
    }
    this.cd.detectChanges();
  }

  get daysLeft(): number {
    return this.info ? Math.max(0, Math.ceil((this.info.expiresAtMs - Date.now()) / 86400000)) : 0;
  }

  get maxGb(): number {
    return this.info ? Math.round(this.info.maxBytes / 1024 ** 3) : 0;
  }

  get doneCount(): number {
    return this.items.filter(i => i.status === 'done').length;
  }

  get failedCount(): number {
    return this.items.filter(i => i.status === 'failed').length;
  }

  get overallPercent(): number {
    return this.items.length ? Math.round((this.items.filter(i => i.status === 'done' || i.status === 'failed').length / this.items.length) * 100) : 0;
  }

  get canChoose(): boolean {
    return this.state === 'ready' || this.state === 'selected';
  }

  chooseFolder() {
    this.folderInput.nativeElement.value = '';
    this.folderInput.nativeElement.click();
  }

  chooseFiles() {
    this.filesInput.nativeElement.value = '';
    this.filesInput.nativeElement.click();
  }

  async onSelected(event: Event, isFolder: boolean) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;
    this.state = 'reading';
    this.message = '';
    this.studies = [];
    this.dicomdirRelativePath = null;
    try {
      let list = Array.from(input.files).map(file => ({
        file,
        relativePath: (isFolder && (file as any).webkitRelativePath) || file.name,
      }));

      // A disc has a DICOMDIR index: read it here to know the studies and to skip files the disc does not reference.
      const dir = list.find(f => f.file.name.toUpperCase() === 'DICOMDIR');
      if (dir) {
        this.dicomdirRelativePath = dir.relativePath;
        try {
          const meta = await this.dicom_$.getDicomdirMetadata(dir.file);
          this.studies = (meta.studiesData || []) as StudyRecord[];
          list = this.filterByDicomdir(list, (meta.filesData || []) as FileRecord[]);
        } catch (err) {
          console.warn('DICOMDIR could not be read; uploading every selected file.', err);
        }
      }

      if (this.info && list.length > this.info.maxFiles) {
        this.state = 'ready';
        this.message = `This link accepts up to ${this.info.maxFiles} files; you selected ${list.length}.`;
        return;
      }
      this.items = list.map(f => ({ ...f, progress: 0, status: 'pending' as const }));
      this.state = 'selected';
    } catch (err) {
      console.error('Could not read the selection', err);
      this.state = 'error';
      this.message = 'We could not read the files you selected. Please try again.';
    } finally {
      this.cd.detectChanges();
    }
  }

  async upload() {
    if (this.state !== 'selected') return;
    this.state = 'uploading';
    this.message = '';
    for (const i of this.items) {
      i.status = 'pending';
      i.progress = 0;
    }
    let next = 0;
    const worker = async () => {
      while (next < this.items.length) {
        await this.uploadOne(this.items[next++]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, this.items.length) }, worker));
    this.state = 'uploaded';
    this.cd.detectChanges();
  }

  /** Retry only what failed (the link is still open until Finish). */
  async retryFailed() {
    const failed = this.items.filter(i => i.status === 'failed');
    if (!failed.length) return;
    this.state = 'uploading';
    for (const i of failed) await this.uploadOne(i);
    this.state = 'uploaded';
    this.cd.detectChanges();
  }

  async finish() {
    if (this.state !== 'uploaded' || this.doneCount === 0) return;
    this.state = 'finishing';
    try {
      await this.links.complete(this.token, {
        studies: this.studies,
        dicomdirRelativePath: this.dicomdirRelativePath,
        failedFiles: this.items.filter(i => i.status === 'failed').map(i => i.relativePath),
      });
      this.receivedCount = this.doneCount;
      this.receivedAt = new Date();
      this.state = 'done';
    } catch (e) {
      console.error('Finish failed', e);
      this.state = 'uploaded';
      this.message = 'We could not finish. Please press Finish again.';
    }
    this.cd.detectChanges();
  }

  private async uploadOne(item: Item, attempt = 1): Promise<void> {
    item.status = 'uploading';
    this.cd.detectChanges();
    try {
      const signed = await this.links.sign(this.token, item.relativePath, item.file.size, item.file.type || 'application/octet-stream');
      await this.put(signed.signedUrl, item.file, signed.contentType, signed.headers, p => {
        item.progress = p;
        this.cd.detectChanges();
      });
      item.progress = 100;
      item.status = 'done';
    } catch (err) {
      const code = (err as any)?.code || '';
      // A dead link or an exhausted limit will not get better by retrying.
      if (attempt < MAX_RETRIES && !String(code).includes('not-found') && !String(code).includes('resource-exhausted')) {
        await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 500));
        return this.uploadOne(item, attempt + 1);
      }
      item.status = 'failed';
      if (String(code).includes('not-found')) this.message = 'This link is no longer valid.';
      if (String(code).includes('resource-exhausted')) this.message = 'This link has reached its upload limit.';
    }
    this.cd.detectChanges();
  }

  private put(url: string, file: File, contentType: string, headers: Record<string, string>, onProgress: (pct: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      Object.keys(headers || {}).forEach(k => xhr.setRequestHeader(k, headers[k]));
      xhr.upload.onprogress = ev => {
        if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)));
      xhr.onerror = () => reject(new Error('network'));
      xhr.send(file);
    });
  }

  private filterByDicomdir(all: { file: File; relativePath: string }[], filesData: FileRecord[]) {
    if (filesData.length === 0) return all;
    const referenced = new Set(filesData.map(f => f.ReferencedFileID.toLowerCase()));
    return all.filter(f => {
      if (f.file.name.toUpperCase() === 'DICOMDIR') return true;
      const rel = f.relativePath.toLowerCase();
      return referenced.has(rel) || [...referenced].some(r => rel.endsWith('/' + r) || rel === r);
    });
  }
}
